import { Code, CodeService } from "@/services/code-service";
import { Button } from "../ui/button";
import { useEffect, useState } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Action } from "@/services/ActionService";
import { formatString } from "@/lib/string-utils";
import { priorityColors, actionStatusColors } from "@/lib/task-utils";
import { Project, ProjectService } from "@/services/project-service";
import { Link, useParams } from "react-router";
import { useForm } from "react-hook-form";
import { ValidationError } from "@/lib/http-client";
import { setFormErrors } from "@/lib/react-form-utils";
import Header from "../Header";
import { featureTypeEditUrlBuilder, featureUrlBuilder, projectEditUrlBuilder } from "@/lib/url-builder";
import { FeatureService } from "@/services/FeatureService";
import type { Feature } from "@/lib/models";
import { FeatureTypeService, type FeatureType } from "@/services/FeatureTypeService";
import { User, UserService } from "@/services/UserService";

export interface ActionEditProps {
  action: Action,
  primaryButtonText: string;
  onSave: (action: Action) => Promise<void>;
}

export const ActionForm: React.FC<ActionEditProps> = ({
  action,
  primaryButtonText,
  onSave
}) => {
  const params = useParams();
  const [codes, setCodes] = useState<Code[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [featureList, setFeatureList] = useState<Feature[]>([])
  const [featureTypeList, setFeatureTypeList] = useState<FeatureType[]>([])
  const [userList, setUserList] = useState<User[]>([])
  const {register, handleSubmit, formState: { isDirty, errors }, reset, watch, setError } = useForm<Omit<Action, "id">>();
  const [priorityCodeId, statusCodeId, projectId, featureId, featureTypeId] = watch(["priorityCodeId", "statusCodeId", "projectId", "featureId", "featureTypeId"])
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [actionName, setActionName] = useState("");

  useEffect(() => {
    setIsSaveDisabled(!isDirty);
  }, [isDirty]);

  useEffect(() => { 
    const loadActtion = async () => {
      if (!action) {
        return
      }

      const [codes, project, features, featureTypes, users]  = await Promise.all([
        CodeService.getCodes2(),
        ProjectService.getBySiteId(params.siteId!),
        FeatureService.getBySiteId(params.siteId!),
        FeatureTypeService.getBySiteId(params.siteId!),
        UserService.getUsers()
      ])

      setCodes(codes)
      setFeatureList(features)
      setProjects(project)
      setActionName(action.name)
      setFeatureTypeList(featureTypes)
      setUserList(users)

      resetAction(action)
    }
    loadActtion()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, params.siteId])

  const resetAction = (action: Action) => {
    reset({
      description: action.description,
      endDate: formatString(action.endDate?.toString()),
      name: action.name,
      featureTypeId: action.featureTypeId ?? null,
      featureId: action.featureId ?? null,
      projectId: action.projectId,
      priorityCodeId: action.priorityCodeId,
      startDate: formatString(action.startDate?.toString()),
      statusCodeId: action.statusCodeId,
      siteId: action.siteId,
      assignedUserId: action.assignedUserId,
    })
  }

  const onSubmit = async (formData: Omit<Action, "id">) => {
    const updatedAction: Action = {
      id: action.id,
      ...formData,
      projectId: formData.projectId === '' ? null : formData.projectId,
      featureTypeId: formData.featureTypeId === '' ? null : formData.featureTypeId,
      featureId: formData.featureId === '' ? null : formData.featureId,
      siteId: action.siteId,
    };

    try {
        await onSave(updatedAction);
        setActionName(formData.name)
        resetAction(updatedAction)
    } catch (error) {
        if (error instanceof ValidationError) {
            setFormErrors<Omit<Action, "id">>(error.errors, setError, formData)
        }
    } finally {
        setIsSaveDisabled(true)
    }
  };

  return (
    <>
      <Header headerTitle={`Task:   ${actionName}`} />
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700"
          >
            Name *
          </label>
          <Input
            type="text"
            {...register("name", { required: 'Name is required'})}
          />
          <span className="text-sm text-red-500">{errors.name?.message}</span>
        </div>
        <div>
          <Label
            htmlFor="projectId"
            className="block text-sm font-medium text-gray-700"
          >
            {projectId ? <Link className="text-blue-500" to={projectEditUrlBuilder(params.siteId!, projectId)}>Project</Link> : <>Project</>}
          </Label>
          <select
            id="projectId"
            className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1"
            {...register("projectId")}
          >
            <option value="">Select project</option>
            {projects
              .map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
          </select>
        </div>
        <div>
          <Label
            htmlFor="featureTypeId"
            className="block text-sm font-medium text-gray-700"
          >
            {featureTypeId ? <Link className="text-blue-500" to={featureTypeEditUrlBuilder(params.siteId!, featureTypeId)}>Feature Type</Link> : <>Feature Type</>}
          </Label>
          <select
            id="featureTypeId"
            className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1"
            {...register("featureTypeId")}
          >
            <option value="">Select feature</option>
            {featureTypeList
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((featureType) => (
                <option key={featureType.id} value={featureType.id!}>
                  {featureType.name}
                </option>
              ))}
          </select>
        </div>          
        <div>
          <Label
            htmlFor="featureId"
            className="block text-sm font-medium text-gray-700"
          >
            {featureId ? <Link className="text-blue-500" to={featureUrlBuilder(params.siteId!, featureId!)}>Feature</Link> : <>Feature</>}
          </Label>
          <select
            id="featureId"
            className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1"
            {...register("featureId")}
          >
            <option value="">Select feature</option>
            {featureList
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((feature) => (
                <option key={feature.id} value={feature.id!}>
                  {feature.name}
                </option>
              ))}
          </select>
        </div>
        <div>
          <Label
            htmlFor="assignedUserId"
            className="block text-sm font-medium text-gray-700"
          >
            Assigned User
          </Label>
          <select
            id="assignedUserId"
            className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1"
            {...register("assignedUserId")}
          >
            <option value="">Select user</option>
            {userList
              .map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
          </select>
        </div>        
        <div>
          <label
            htmlFor="priorityCodeId"
            className="block text-sm font-medium text-gray-700"
          >
            Priority
          </label>
          <select
            className={`flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 ${priorityColors[priorityCodeId]}`}
            {...register("priorityCodeId", { required: 'Status is required'})}
          >
              {codes.length > 0 && (
                <>
                  {codes
                    .filter((c) => c.type === "ActionPriority")
                    .map((priorityCode) => (
                      <option key={priorityCode.id} value={priorityCode.id} className={priorityColors[priorityCode.id]}>
                        {priorityCode.name}
                      </option>
                    ))}
                </>
              )}
          </select>
        </div>
        <div>
          <Label
            htmlFor="statusCodeId"
            className="block text-sm font-medium text-gray-700"
          >
            Status
          </Label>
          <select
            id="statusCodeId"
            className={`flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 ${actionStatusColors[statusCodeId]}`}
            {...register("statusCodeId", { required: 'Status is required'})}
          >
              {codes.length > 0 && (
                <>
                  {codes
                    .filter((c) => c.type === "ActionStatus")
                    .map((statusCode) => (
                      <option key={statusCode.id} value={statusCode.id ?? ""} className={actionStatusColors[statusCode.id]}>
                        {statusCode.name}
                      </option>
                    ))}
                </>
              )}
          </select>
        </div>
        <div>
          <Label
            htmlFor="startDate"
            className="block text-sm font-medium text-gray-700"
          >
            Start Date
          </Label>
          <Input
            type="date"
            id="startDate"
            {...register("startDate", { required: 'Start date is required'})}
            className="block"
          />
        </div>
        <div>
          <Label
            htmlFor="endDate"
            className="block text-sm font-medium text-gray-700"
          >
            End Date
          </Label>
          <Input
            type="date"
            id="endDate"
            {...register("endDate", { required: 'End date is required'})}
            className="block"
          />
          <span className="text-sm text-red-500">{errors.endDate?.message}</span>
        </div>
        <div className="flex justify-end space-x-2">
          <button
            type="button"
            onClick={() => reset()}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <Button
            type="submit"
            disabled={isSaveDisabled}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {primaryButtonText}
          </Button>
        </div>
      </form>
    </>
  );
};
